import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { Drawer, DrawerContent } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import type { ReturnsLineItem } from '@itsrever/rever-api'
import { useTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { calculateUnitPrice } from '../helpers/calculate-unit-price'
import { ItemFlow } from '../item-flow/item-flow'
import { ItemAction } from './item-action'

interface ItemCardProps {
  lineItem: ReturnsLineItem
  subProductId: number
  isBlocked: boolean
}

export const ItemCard = ({
  lineItem,
  isBlocked,
  subProductId
}: ItemCardProps) => {
  const { i18n } = useTranslation()

  const { translate } = useTranslate()

  const [searchParams, setSearchParams] = useSearchParams()

  const [open, setOpen] = useState(false)

  const isFullCatalogOpen = searchParams.get('catalog') === 'true'

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const itemsToReturn = useAppSelector((store) => store.return.returnedItems)

  const returnedItem = itemsToReturn.find(
    (retItem) =>
      retItem.item.line_item_id === lineItem.id &&
      retItem.subProductId === subProductId
  )

  const isBeingReturned = !!returnedItem

  const openItemFlow = (open: boolean) => {
    setOpen(open)

    if (!open && !isFullCatalogOpen) {
      searchParams.delete('lineItemId')
      setSearchParams(searchParams)

      return
    }

    searchParams.set('lineItemId', lineItem.id ?? '')
    setSearchParams(searchParams)
  }

  const price = calculateUnitPrice(
    { quantity: lineItem.quantity, total: lineItem.total },
    { amount_multiplied_by: moneyFormat?.amount_multiplied_by }
  )

  const image = lineItem.product?.images?.[0]

  return (
    <>
      <div
        data-selected={!!isBeingReturned}
        data-blocked={isBlocked}
        className="group rounded-xl shadow-sm transition-shadow hover:shadow-md data-[blocked=true]:hover:shadow-sm data-[selected=true]:hover:shadow-sm"
      >
        <div
          data-selected={!!isBeingReturned}
          className="relative rounded-xl bg-white p-2.5 data-[selected=true]:rounded-b-none"
        >
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="flex gap-3 group-data-[blocked=true]:opacity-65">
              <div className="relative size-[5.5rem] shrink-0 overflow-hidden rounded-md">
                {image ? (
                  <img
                    height={88}
                    width={88}
                    src={image.src}
                    alt={image.alt}
                    className="size-full object-cover"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500"
                  >
                    <MediaImageIcon width={20} height={20} />
                  </div>
                )}
                {lineItem.quantity && lineItem.quantity > 1 ? (
                  <span className="absolute bottom-1 right-1 rounded-sm bg-white/50 p-0.5 px-1 text-xs font-medium backdrop-blur-sm">
                    x {lineItem.quantity}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-col py-1">
                <button
                  disabled={!!isBeingReturned || isBlocked}
                  className="line-clamp-2 text-left text-sm font-normal before:absolute before:inset-0"
                  onClick={() => openItemFlow(true)}
                >
                  {lineItem.name}
                </button>
                <p className="text-xs font-normal text-neutral-500">
                  {lineItem.variant_name}
                </p>
                <p className="mt-auto text-sm font-normal">
                  {price.toLocaleString(i18n.language, {
                    style: 'currency',
                    currency: moneyFormat?.currency
                  })}
                </p>
              </div>
            </div>
            {isBlocked ? (
              <div className="ml-auto max-w-fit md:max-w-64">
                <p
                  className="rounded-md border border-solid border-yellow-700 px-2 py-1 text-xs text-yellow-700 md:text-xs"
                  dangerouslySetInnerHTML={{
                    __html: translate('item_selection_reason_multilabel')
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        {isBeingReturned ? <ItemAction returnedItem={returnedItem} /> : null}
      </div>
      <Drawer
        isOpen={open}
        onOpenChange={openItemFlow}
        backdrop="blur"
        radius="sm"
        hideCloseButton
        classNames={{
          wrapper: 'h-dvh max-h-dvh',
          backdrop: 'backdrop-blur-[2px] bg-black/10',
          base: 'rounded-none md:rounded-md md:right-2 md:top-2 md:bottom-2 w-full max-w-full md:max-w-[500px] h-dvh max-h-dvh md:h-auto pb-2 md:pb-0'
        }}
      >
        <DrawerContent>
          {(onClose) => (
            <ItemFlow
              lineItem={lineItem}
              subProductId={subProductId}
              onClose={onClose}
            />
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}
